import React from "react";
import { Alert, Box, Stack, Typography } from "@mui/material";
import { Info } from "@phosphor-icons/react";
import { useFormikContext } from "formik";

import LayerCard from "../elements/LayerCard";
import type { TargetFormValues } from "../TargetBuilderPanel.types";
import { getNetCountMode } from "../TargetBuilderPanel.types";
import { getAnalyticsItems } from "./analyticsToggleOptions";
import type { PrimitiveType } from "api/types/charts";

/**
 * L4 — Derived Metrics preview.
 *
 * Read-only informational card. The catalogue of metrics surfaced here is
 * driven by `ANALYTICS_BY_L1_L3` in `./analyticsByL1L3.ts` — this component
 * stays focused on rendering.
 *
 * Nothing here is persisted to the API: it's a hint to the clinician about
 * what the platform will compute from collected session data.
 */
interface AnalyticsPreviewProps {
  /** Goal behavior subject controls caregiver-specific analytics availability. */
  behaviorSubject?: "client" | "caregiver";
}

const AnalyticsPreview: React.FC<AnalyticsPreviewProps> = ({ behaviorSubject = "client" }) => {
  const { values } = useFormikContext<TargetFormValues>();
  const { l1_dimension, l3_context, teaching_procedure, target_type } = values;

  if (!l1_dimension || !l3_context) return null;

  const promptHierarchy =
    teaching_procedure && "prompt_hierarchy" in teaching_procedure
      ? teaching_procedure.prompt_hierarchy
      : undefined;
  const promptLevels = promptHierarchy?.levels ?? [];
  const hasPromptHierarchy = promptLevels.length > 0;
  const countPromptedAsCorrect = promptHierarchy?.count_prompted_as_correct === true;

  const primitiveType = (() => {
    if (l1_dimension === "magnitude") return "rating_scale";
    if (l1_dimension === "duration") return "timer";
    if (l1_dimension === "latency") {
      if (l3_context === "free-operant") return "latency";
      return hasPromptHierarchy ? "latency_prompt_hierarchy" : "latency";
    }
    if (l1_dimension !== "frequency") return null;

    if (l3_context === "task-analysis") return "task_analysis";
    if (l3_context === "free-operant") return "counter_occurrence";

    if (l3_context === "discrete-trial") {
      return hasPromptHierarchy ? "counter_prompt_hierarchy" : "counter_correct_incorrect";
    }

    if (l3_context === "net") {
      const mode = getNetCountMode(teaching_procedure, l1_dimension, target_type);
      if (hasPromptHierarchy) return "counter_prompt_hierarchy";
      return mode === "correct_incorrect" ? "counter_correct_incorrect" : "counter_occurrence";
    }

    return null;
  })() as PrimitiveType | null;

  const items = getAnalyticsItems(l1_dimension, l3_context, {
    primitiveType,
    hasPromptHierarchy,
    countPromptedAsCorrect,
    behaviorSubject,
    targetType: target_type,
  });

  return (
    <LayerCard
      title="Here's what you'll be able to see"
      description="Based on your configuration, these analytics will be automatically calculated"
    >
      {items ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
            gap: 2,
          }}
        >
          {items.map((item) => (
            <Stack
              key={item.title}
              direction="row"
              alignItems="flex-start"
              gap={2}
              p={3}
              sx={(theme) => ({
                bgcolor: (theme.palette as any).grayscale?.[50] ?? "#F6F7F8",
                borderRadius: 2,
              })}
            >
              <Box
                sx={(theme) => ({
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "background.paper",
                  color: "text.secondary",
                  flexShrink: 0,
                  border: "1px solid",
                  borderColor: (theme.palette as any).grayscale?.[100] ?? "#E2E8F0",
                })}
                aria-hidden
              >
                {item.icon}
              </Box>
              <Stack gap={1} minWidth={0}>
                <Typography variant="body2" fontWeight={500}>
                  {item.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {item.description}
                </Typography>
              </Stack>
            </Stack>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Analytics for this configuration will be available soon.
        </Typography>
      )}

      <Alert
        severity="info"
        variant="standard"
        icon={<Info size={20} weight="regular" />}
        sx={{ mt: 2 }}
      >
        These analytics are automatically derived from the data you collect. You&apos;ll see them on
        the progress dashboard after sessions are logged.
      </Alert>
    </LayerCard>
  );
};

export default AnalyticsPreview;
